import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Clear,
  Search,
  Print,
  Login,
  RestartAlt,
  Logout,
  PanTool,
  Flag,
} from '@mui/icons-material';
import { useFirebaseContext } from '../lib/firebase-context';
import { Attendee } from '../lib/types';
import {
  checkInAttendee,
  checkOutAttendee,
  updateAttendee,
  useActor,
} from '../lib/audited-writes';
import { useAppContext } from '../lib/app-context';
import {
  generateInlineLabelUrl,
  generateLabelUrl,
  printLabel,
} from '../lib/printer-api';
import { cacheLabelUrl } from '../lib/writes/label-cache';
import { useUserContext } from '../lib/users-context';
import { NO_PRINT_ID } from '../lib/constants';
import {
  formatGroupDisplay,
  formatGroupForLabel,
  formatGroupShort,
  sortGroups,
} from '../lib/groups';
import { flagDetail, flagOn } from '../lib/attendee-flags';
import AttendeeForm from './attendee-form';
import {
  EmergencyContactBlock,
  FlagChip,
  TwoLines,
  matchesSearchQuery,
} from './attendees-list.utils';
import { useDebouncedValue } from '../lib/hooks/use-debounced-value';

const AttendeeItem = memo(function AttendeeItem({
  attendee,
  onSelect,
}: {
  attendee: Attendee;
  onSelect: (attendee: Attendee) => void;
}) {
  const actor = useActor();
  const { isAdmin } = useUserContext();
  const { activeEvent, activeCheckInDate, groups, customFields } =
    useFirebaseContext();
  const { printerId, adminMode } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  // Local mirror of `attendee.groupRef` so the admin-mode inline Select can
  // update optimistically before the Firestore snapshot returns. The effect
  // below resyncs it whenever the prop changes — without it, edits made via
  // the AttendeeForm dialog (or by another user) wouldn't update this row's
  // group label until the component remounted, since `useState`'s initializer
  // only runs once.
  const [groupId, setGroupId] = useState(attendee.groupRef || '');
  useEffect(() => {
    setGroupId(attendee.groupRef || '');
  }, [attendee.groupRef]);

  const showAdminElements = useMemo(
    () => isAdmin && adminMode,
    [isAdmin, adminMode]
  );

  const isCheckedIn = useMemo(
    () => activeCheckInDate?.checkedInRefs?.includes(attendee.ref),
    [activeCheckInDate, attendee.ref]
  );
  const isCheckedOut = useMemo(
    () => activeCheckInDate?.checkedOutRefs?.includes(attendee.ref),
    [activeCheckInDate, attendee.ref]
  );
  const hideCheckout = Boolean(activeEvent?.hideCheckout);

  const printAttendeeLabel = useCallback(
    async (keepLoadingState?: boolean) => {
      // printerId 0 is no selected printer, NO_PRINT_ID is do not print
      if (!activeCheckInDate || printerId === 0 || printerId === NO_PRINT_ID)
        return;
      if (!keepLoadingState) {
        setIsLoading(true);
      }
      try {
        // Set the label URL to the existing one if available
        let labelUrl = attendee.s3LabelUrl;
        // If the label URL is not available, generate it
        if (!attendee.s3LabelUrl) {
          // Surface the attendee's "Color" custom-field value to the label
          // generator as a `customBadges` entry so it can render a colored
          // chip on the printed badge. We look up the field by name (the
          // backend doesn't know our customField doc refs). If the field
          // doesn't exist or the attendee has no value for it, we send
          // nothing and the payload omits `customBadges`.
          const colorField = customFields.find(
            (f) => f.name.toLowerCase() === 'color',
          );
          const colorValue = colorField
            ? attendee.customFields?.[colorField.ref]
            : undefined;
          const badges = colorValue ? [colorValue] : undefined;
          if (activeEvent?.foldGradeIntoGroupName) {
            // Fold path: the backend clobbers any inline field whenever
            // `attendeeId` is present, so the folded class line can only
            // reach it if we send every field ourselves and omit the id.
            // That also means the backend won't persist the URL for us —
            // hence the `cacheLabelUrl` write below.
            const group = groups.find((g) => g.ref === attendee.groupRef);
            const res = await generateInlineLabelUrl({
              firstName: attendee.first_name,
              lastName: attendee.last_name,
              groupName: group ? formatGroupForLabel(group) : '',
              teacherNames: group?.teachers,
              medicalConcerns: attendee.medical_concerns,
              foodAllergies: attendee.food_allergies,
              customBadges: badges,
            });
            labelUrl = res.s3LabelUrl;
            // Best-effort — a failure here only costs a regenerated PDF
            // on the next print, so it never blocks printing this one.
            if (labelUrl) {
              await cacheLabelUrl(attendee.ref, labelUrl);
            }
          } else {
            // `printGrade` is an event-level setting on the active event.
            // Forwarded so the backend can omit the grade chip when the
            // admin has opted out. See `AppEvent.printGrade` and
            // `buildCreateLabelPdfBody`.
            const res = await generateLabelUrl(
              attendee.ref,
              badges,
              activeEvent?.printGrade,
            );
            // Set the label URL from the response
            labelUrl = res.s3LabelUrl;
          }
        }
        // Making sure the label URL is there before printing
        if (labelUrl) {
          await printLabel(labelUrl, printerId);
        }
      } catch (error) {
        console.error('Error printing label', error);
      }
      if (!keepLoadingState) {
        setIsLoading(false);
      }
    },
    [
      attendee.s3LabelUrl,
      attendee.ref,
      attendee.customFields,
      attendee.first_name,
      attendee.last_name,
      attendee.groupRef,
      attendee.medical_concerns,
      attendee.food_allergies,
      activeEvent?.printGrade,
      activeEvent?.foldGradeIntoGroupName,
      activeCheckInDate,
      printerId,
      customFields,
      groups,
    ]
  );

  const toggleCheckIn = useCallback(async () => {
    if (!activeCheckInDate) return;
    setIsLoading(true);
    try {
      if (!isCheckedIn) {
        printAttendeeLabel(true);
      }
      await checkInAttendee(
        actor,
        activeCheckInDate,
        attendee,
        isCheckedIn ? 'uncheck-in' : 'check-in',
      );
    } catch (error) {
      console.error('Error checking in', error);
    }
    setIsLoading(false);
  }, [isCheckedIn, activeCheckInDate, attendee, actor, printAttendeeLabel]);

  const toggleCheckOut = useCallback(async () => {
    if (!activeCheckInDate) return;
    setIsLoading(true);
    try {
      await checkOutAttendee(
        actor,
        activeCheckInDate,
        attendee,
        isCheckedOut ? 'uncheck-out' : 'check-out',
      );
    } catch (error) {
      console.error('Error checking out', error);
    }
    setIsLoading(false);
  }, [isCheckedOut, activeCheckInDate, attendee, actor]);

  const handleSelectGroup = useCallback(
    async (e: any) => {
      if (!isAdmin) return;
      setIsLoading(true);
      setGroupId(e.target.value as string);

      try {
        await updateAttendee(actor, attendee, { groupRef: e.target.value });
      } catch (error) {
        console.error('Error updating group', error);
      }
      setIsLoading(false);
    },
    [isAdmin, attendee, actor]
  );

  const groupForDisplay = useMemo(
    () => groups.find((group) => group.ref === groupId),
    [groupId, groups]
  );

  const groupLines = useMemo(() => {
    if (!groupForDisplay) return { line1: 'No group', line2: '' };
    return formatGroupDisplay(groupForDisplay);
  }, [groupForDisplay]);

  return (
    <ListItem
      disableGutters
      sx={({ palette }) => ({ ':hover': { background: palette.action.hover } })}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '180px 130px 200px 215px 1fr',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Button
            variant='text'
            type='button'
            sx={{
              textTransform: 'none',
              justifyContent: 'flex-start',
              textAlign: 'left',
              // Allow long names to wrap inside the 180px column instead of
              // overflowing into the chips column. `overflow-wrap: anywhere`
              // covers the rare unbroken-token case (no spaces or hyphens)
              // by allowing a mid-word break only when there is no other
              // option. `whiteSpace: normal` re-enables wrapping on the
              // Button's inner span, which MUI sets to nowrap by default.
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              lineHeight: 1.25,
            }}
            onClick={() => onSelect(attendee)}
          >
            {attendee.last_name}, {attendee.first_name}
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          {attendee.ui_added ? (
            <Tooltip title='Manually added' placement='top'>
              <Box
                component='span'
                tabIndex={0}
                sx={{ display: 'inline-flex' }}
              >
                <PanTool
                  fontSize='small'
                  sx={{ height: '0.8em', width: '0.8em' }}
                  color='success'
                />
              </Box>
            </Tooltip>
          ) : null}
          {attendee.notes?.trim() ? (
            <Tooltip
              title={attendee.notes}
              placement='top'
              slotProps={{
                tooltip: { sx: { whiteSpace: 'pre-wrap' } },
              }}
            >
              <Box
                component='span'
                tabIndex={0}
                sx={{ display: 'inline-flex' }}
              >
                <Flag
                  fontSize='small'
                  sx={{ height: '0.8em', width: '0.8em' }}
                  color='error'
                />
              </Box>
            </Tooltip>
          ) : null}
          <FlagChip
            label='FA'
            on={flagOn(attendee.food_allergies)}
            detail={flagDetail(attendee.food_allergies)}
          />
          <FlagChip
            label='MC'
            on={flagOn(attendee.medical_concerns)}
            detail={flagDetail(attendee.medical_concerns)}
          />
          {customFields
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            )
            .map((f) => {
              const value = attendee.customFields?.[f.ref];
              if (!value) return null;
              return (
                <Tooltip key={f.ref} title={f.name} placement='top'>
                  <Chip
                    label={value}
                    size='small'
                    variant='outlined'
                    sx={{ fontSize: '0.7em', height: '20px' }}
                  />
                </Tooltip>
              );
            })}
        </Box>
        <Box
          sx={{
            width: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <EmergencyContactBlock attendee={attendee} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          {showAdminElements ? (
            <FormControl
              size='small'
              sx={{ width: 180 }}
              disabled={isLoading}
            >
              <InputLabel id='group-label'>Group</InputLabel>
              <Select
                labelId='group-label'
                id='group-select'
                value={groupId}
                label='Group'
                onChange={handleSelectGroup}
              >
                <MenuItem value='' disabled>
                  Select group
                </MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.ref} value={group.ref}>
                    {formatGroupShort(group)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box
              sx={{
                width: 180,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <TwoLines line1={groupLines.line1} line2={groupLines.line2} />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
          }}
        >
          {activeCheckInDate && (
            <>
              {isCheckedIn ? (
                <Tooltip
                  title={
                    printerId === 0 ? 'Please select a printer' : 'Reprint label'
                  }
                  followCursor
                >
                  <span>
                    <IconButton
                      size='small'
                      color='secondary'
                      disabled={isLoading || printerId === 0}
                      onClick={() => printAttendeeLabel()}
                      aria-label='Reprint label'
                    >
                      <Print fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Box sx={{ width: 30, height: 30 }} aria-hidden />
              )}
              <ButtonGroup
                disabled={isLoading}
                size='small'
                sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}
              >
                <Tooltip
                  title={printerId === 0 ? 'Please select a printer' : ''}
                  followCursor
                >
                  <span>
                    <Button
                      onClick={toggleCheckIn}
                      startIcon={isCheckedIn ? <RestartAlt /> : <Login />}
                      disabled={
                        printerId === 0 || (!hideCheckout && isCheckedOut)
                      }
                      color={isCheckedIn ? 'warning' : 'success'}
                    >
                      {isCheckedIn ? 'Reset check-in' : 'Check in'}
                    </Button>
                  </span>
                </Tooltip>
                {!hideCheckout && (
                  <Button
                    onClick={toggleCheckOut}
                    startIcon={isCheckedOut ? <RestartAlt /> : <Logout />}
                    disabled={!isCheckedIn}
                    color={isCheckedOut ? 'warning' : 'primary'}
                  >
                    {isCheckedOut ? 'Reset check-out' : 'Check out'}
                  </Button>
                )}
              </ButtonGroup>
            </>
          )}
        </Box>
      </Box>
    </ListItem>
  );
});

const AttendeesList = () => {
  const { attendees, groups, activeCheckInDate, activeEvent } =
    useFirebaseContext();
  const { activeView, setActiveView } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroupRef, setSelectedGroupRef] = useState('');
  const [activeAttendee, setActiveAttendee] = useState<Attendee | null>(null);

  // Keep typing responsive: render the input synchronously, but only
  // recompute the filtered list 300ms after the last keystroke. The
  // attendee list can be hundreds of rows, so debouncing reduces churn
  // on the (memoized) AttendeeItem children while a volunteer is typing.
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  const sortedGroups = useMemo(() => sortGroups(groups), [groups]);

  // Preserve previous behavior: leaving the "total" view clears the search box.
  useEffect(() => {
    if (activeCheckInDate && activeView !== 'total' && searchQuery !== '') {
      setSearchQuery('');
    }
  }, [activeCheckInDate, activeView, searchQuery]);

  // Firestore returns attendees ordered by first_name (see
  // getAttendeesForEventQuery); the Check-in page prefers last-name order
  // with first-name tiebreaker, matching the printed Roster.
  const sortedAttendees = useMemo(
    () =>
      attendees.slice().sort((a, b) => {
        const lastCmp = (a.last_name ?? '').localeCompare(
          b.last_name ?? '',
          undefined,
          { sensitivity: 'base' },
        );
        if (lastCmp !== 0) return lastCmp;
        return (a.first_name ?? '').localeCompare(
          b.first_name ?? '',
          undefined,
          { sensitivity: 'base' },
        );
      }),
    [attendees],
  );

  const filteredAttendees = useMemo(() => {
    const lowerQuery = debouncedSearchQuery.trim().toLowerCase();
    const matchesGroup = (a: Attendee) =>
      !selectedGroupRef || a.groupRef === selectedGroupRef;

    if (!activeCheckInDate || activeView === 'total') {
      return sortedAttendees.filter(
        (a) => matchesSearchQuery(a, lowerQuery) && matchesGroup(a)
      );
    }

    const checkedOutSet = new Set(activeCheckInDate.checkedOutRefs ?? []);
    const checkedInSet = new Set(activeCheckInDate.checkedInRefs ?? []);

    if (activeView === 'checkedIn') {
      // With checkout hidden, "Checked In" means "came today" — don't
      // exclude kids left in `checkedOutRefs` from before the event's
      // `hideCheckout` flag was turned on.
      return sortedAttendees.filter(
        (a) =>
          checkedInSet.has(a.ref) &&
          (activeEvent?.hideCheckout || !checkedOutSet.has(a.ref)) &&
          matchesGroup(a)
      );
    }
    if (activeView === 'checkedOut') {
      return sortedAttendees.filter(
        (a) => checkedOutSet.has(a.ref) && matchesGroup(a)
      );
    }
    if (activeView === 'pending') {
      return sortedAttendees.filter(
        (a) =>
          !checkedInSet.has(a.ref) &&
          !checkedOutSet.has(a.ref) &&
          matchesGroup(a)
      );
    }
    return sortedAttendees;
  }, [
    sortedAttendees,
    debouncedSearchQuery,
    selectedGroupRef,
    activeCheckInDate,
    activeView,
    activeEvent?.hideCheckout,
  ]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setActiveView('total');
    },
    [setActiveView]
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleSelectAttendee = useCallback((attendee: Attendee) => {
    setActiveAttendee(attendee);
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', mt: 3, gap: 2, alignItems: 'center' }}>
        <TextField
          value={searchQuery}
          onChange={handleSearch}
          label='Search'
          fullWidth
          autoComplete='off'
          inputProps={{
            autoCorrect: 'off',
            autoCapitalize: 'off',
            spellCheck: false,
            // Suppress password-manager + browser suggestion overlays
            // (1Password, LastPass, Safari/Chrome autofill).
            'data-1p-ignore': 'true',
            'data-lpignore': 'true',
            'data-form-type': 'other',
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery.length ? (
              <IconButton
                aria-label='Clear search'
                onClick={handleClearSearch}
              >
                <Clear />
              </IconButton>
            ) : null,
          }}
          variant='outlined'
        />
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id='filter-group-label'>Group</InputLabel>
          <Select
            labelId='filter-group-label'
            value={selectedGroupRef}
            label='Group'
            onChange={(e) => setSelectedGroupRef(e.target.value as string)}
          >
            <MenuItem value=''>
              <em>— All groups —</em>
            </MenuItem>
            {sortedGroups.map((group) => (
              <MenuItem key={group.ref} value={group.ref}>
                {formatGroupShort(group)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedGroupRef && (
          <Tooltip title='Clear group filter' placement='top'>
            <IconButton
              aria-label='Clear group filter'
              onClick={() => setSelectedGroupRef('')}
            >
              <Clear />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <List>
        {filteredAttendees.map((attendee) => (
          <AttendeeItem
            key={attendee.ref}
            attendee={attendee}
            onSelect={handleSelectAttendee}
          />
        ))}
      </List>
      <Dialog
        open={Boolean(activeAttendee)}
        onClose={() => setActiveAttendee(null)}
        fullWidth
      >
        {activeAttendee && (
          <>
            <DialogTitle>
              Edit {activeAttendee.first_name} {activeAttendee.last_name}
            </DialogTitle>
            <DialogContent>
              <AttendeeForm
                attendee={activeAttendee}
                onClose={() => setActiveAttendee(null)}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default AttendeesList;
